import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./client";

export type AccountHealth = "online" | "limited" | "paused" | "banned" | "idle";
export type ProxyType = "socks4" | "socks5" | "http" | "https";

export interface AccountProxy {
  id: number;
  proxy_type: Exclude<ProxyType, "https">;
  host: string;
  port: number;
  username: string | null;
  has_password: boolean;
  country_code: string | null;
  is_alive: boolean;
  fail_count: number;
  last_checked_at: string | null;
  last_failed_at: string | null;
  last_error: string | null;
}

export interface AccountProxyInput {
  proxy_type: ProxyType;
  host: string;
  port: number;
  username?: string | null;
  password?: string | null;
  country_code?: string | null;
}

export interface Account {
  id: number;
  name: string;
  phone: string;
  is_enabled: boolean;
  is_premium: boolean;
  is_banned: boolean;
  ban_reason: string | null;
  last_seen_at: string | null;
  last_flood_at: string | null;
  last_flood_seconds: number;
  proxy_country: string | null;
  proxy: AccountProxy | null;
  avatar_url: string | null;
  health: AccountHealth;
  device_model: string;
  system_version: string;
  app_version: string;
  lang_code: string;
  system_lang_code: string;
  created_at: string;
}

export interface AccountDialog {
  peer_id: number;
  peer_type: "channel" | "chat";
  title: string;
  chat_ref: string;
  is_existing: boolean;
  can_write: boolean;
}

export interface StartSessionRequest {
  phone: string;
  proxy?: AccountProxyInput | null;
}

export interface StartSessionResponse {
  session_id: string;
  code_length: number;
  resend_in_seconds: number;
}

export type SubmitCodeResponse =
  | { kind: "needs_password" }
  | { kind: "done"; account: Account }
  | { kind: "invalid_code" };

export type SubmitPasswordResponse =
  | { kind: "done"; account: Account }
  | { kind: "invalid_password" };

export function useAccounts() {
  return useQuery<Account[]>({
    queryKey: ["accounts"],
    queryFn: () => api.get<Account[]>("/accounts"),
    staleTime: 15_000,
  });
}

export function useAccountDialogs(accountId: number | null) {
  return useQuery<AccountDialog[]>({
    queryKey: ["accounts", accountId, "dialogs"],
    queryFn: () => api.get<AccountDialog[]>(`/accounts/${accountId}/dialogs`),
    enabled: accountId != null,
    staleTime: 60_000,
  });
}

export function useStartSession() {
  return useMutation({
    mutationFn: (body: StartSessionRequest) =>
      api.post<StartSessionResponse>("/accounts/sessions", body),
  });
}

export function useSubmitCode(sessionId: string) {
  return useMutation({
    mutationFn: (code: string) =>
      api.post<SubmitCodeResponse>(`/accounts/sessions/${sessionId}/code`, { code }),
  });
}

export function useSubmitPassword(sessionId: string) {
  return useMutation({
    mutationFn: (password: string) =>
      api.post<SubmitPasswordResponse>(`/accounts/sessions/${sessionId}/password`, { password }),
  });
}

export function useResendCode(sessionId: string) {
  return useMutation({
    mutationFn: () => api.post<StartSessionResponse>(`/accounts/sessions/${sessionId}/resend`),
  });
}

export function useDeleteAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/accounts/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["accounts"] }),
  });
}

export function useToggleAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, enabled }: { id: number; enabled: boolean }) =>
      api.patch<Account>(`/accounts/${id}`, { is_enabled: enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["accounts"] }),
  });
}

export function useUpdateAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: { name?: string; is_enabled?: boolean; proxy?: AccountProxyInput | null } }) =>
      api.patch<Account>(`/accounts/${id}`, body),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["accounts"] });
      qc.setQueryData(["accounts", data.id], data);
    },
  });
}

export function useRenameAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) =>
      api.patch<Account>(`/accounts/${id}`, { name }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["accounts"] }),
  });
}

export function useCheckAccountProxy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api.post<{ ok: boolean; account: Account }>(`/accounts/${id}/proxy/check`),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["accounts"] });
      qc.setQueryData(["accounts", data.account.id], data.account);
    },
  });
}
